import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const LOGO_URL = "https://customer-assets-7cd3h4nn.emergentagent.net/job_regulated-payments/artifacts/nnv85px6_Easysend-logo-removebg.png";

const navLinks = [
  { name: 'Home', href: '#home' },
  { name: 'Platform', href: '#platform' },
  { name: 'Solutions', href: '#solutions' },
  { name: 'Industries', href: '#industries' },
  { name: 'Company', href: '#company' },
  { name: 'API', href: '#api' },
  { name: 'Contact', href: 'mailto:payments@easysendservices.com' },
];

export const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const scrollToSection = (hash) => {
    const element = document.querySelector(hash);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleNavClick = (e, href) => {
    setMobileMenuOpen(false);
    if (href.startsWith('#')) {
      e.preventDefault();
      if (isHomePage) {
        scrollToSection(href);
      } else {
        navigate('/');
        setTimeout(() => scrollToSection(href), 300);
      }
    }
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    if (isHomePage) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  return (
    <nav 
      className="fixed top-0 left-0 right-0 z-50 navbar-glass border-b border-[#E5E7EB]"
      data-testid="navbar"
    >
      <div className="section-container">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <a 
            href="/" 
            className="flex items-center"
            data-testid="navbar-logo"
            onClick={handleLogoClick}
          >
            <img 
              src={LOGO_URL} 
              alt="Easysend" 
              className="h-8 lg:h-10 w-auto"
            />
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="text-sm font-semibold text-[#1E3A5F] hover:text-[#4FB3DF] transition-colors duration-200"
                data-testid={`navbar-link-${link.name.toLowerCase()}`}
              >
                {link.name}
              </a>
            ))}
          </div>

          {/* Login Button */}
          <div className="hidden lg:block">
            <a
              href="#"
              className="inline-flex items-center justify-center px-5 py-2.5 text-sm font-medium bg-[#4FB3DF] text-white hover:bg-[#3d9ec7] transition-colors duration-200"
              data-testid="navbar-login-button"
            >
              Login
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="navbar-mobile-menu-button"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-[#1E3A5F]" strokeWidth={1.5} />
            ) : (
              <Menu className="w-6 h-6 text-[#1E3A5F]" strokeWidth={1.5} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden bg-white border-b border-[#E5E7EB]"
            data-testid="navbar-mobile-menu"
          >
            <div className="section-container py-6 space-y-4">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className="block text-base font-medium text-[#1E3A5F] hover:text-[#4FB3DF] transition-colors duration-200"
                  data-testid={`navbar-mobile-link-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </a>
              ))}
              <a
                href="#"
                className="inline-flex items-center justify-center w-full px-5 py-3 mt-4 text-sm font-medium bg-[#4FB3DF] text-white hover:bg-[#3d9ec7] transition-colors duration-200"
                data-testid="navbar-mobile-login-button"
              >
                Login
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
